import { useEffect, useMemo, useState } from 'react';
import { Avatar, Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useApiRequests } from 'hooks';
import { StaffItem } from 'types';
import { HeavyCheckIcon, CircleCrossIcon } from 'assets';
import { useAvailability } from 'context/AvailabilityProvider';

import styles from './VIA3HomePage.module.scss';

function isStaffAvailable(availability: Record<string, any>, clinicUserId: string): boolean {
  const a = availability[clinicUserId];
  if (!a) return false;
  return a.isProviderInQueue === true && a.unavailable !== true;
}

export function VIA3HomePage() {
  const { t } = useTranslation('providerRoom');
  const navigate = useNavigate();
  const staffLoginUrl = process.env.REACT_APP_HOME_PAGE_FOR_NON_AUTH_URL;
  const { getPublicStaff } = useApiRequests();

  const [staff, setStaff] = useState<StaffItem[]>([]);
  const availability = useAvailability();

  const visibleStaff = useMemo(() => staff.filter((s) => s.via3UserType !== 'Member'), [staff]);

  const sortedStaff = useMemo(() => {
    return [...visibleStaff].sort((a, b) => {
      const aAvailable = isStaffAvailable(availability, a.clinicUserId);
      const bAvailable = isStaffAvailable(availability, b.clinicUserId);

      if (aAvailable && !bAvailable) return -1;
      if (!aAvailable && bAvailable) return 1;

      return (a.name ?? '').localeCompare(b.name ?? '');
    });
  }, [visibleStaff, availability]);

  useEffect(() => {
    getPublicStaff()
      .then((res) => setStaff(res.items ?? []))
      .catch(() => setStaff([]));
  }, []);

  const availableStaff = useMemo(
    () => sortedStaff.filter((s) => isStaffAvailable(availability, s.clinicUserId)),
    [sortedStaff, availability],
  );

  const unavailableStaff = useMemo(
    () => sortedStaff.filter((s) => !isStaffAvailable(availability, s.clinicUserId)),
    [sortedStaff, availability],
  );

  useEffect(() => {
    const syncForcedLightTheme = () => {
      const shouldForceLightTheme = localStorage.getItem('app-theme') === null;
      if (shouldForceLightTheme) {
        document.body.classList.add('via3-home-force-light');
      } else {
        document.body.classList.remove('via3-home-force-light');
      }
    };

    const handleThemeChange = () => {
      syncForcedLightTheme();
    };

    syncForcedLightTheme();
    window.addEventListener('app-theme-change', handleThemeChange as EventListener);
    window.addEventListener('storage', handleThemeChange);

    return () => {
      window.removeEventListener('app-theme-change', handleThemeChange as EventListener);
      window.removeEventListener('storage', handleThemeChange);
      document.body.classList.remove('via3-home-force-light');
    };
  }, []);

  const renderCard = (s: StaffItem) => {
    const isAvailable = isStaffAvailable(availability, s.clinicUserId);

    const getInitials = (name?: string) => {
      if (!name) return '';
      const parts = name.trim().split(' ').filter(Boolean);

      if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
      }

      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    };

    return (
      <Card key={s.clinicUserId} className={styles.staffCard}>
        <CardContent className={styles.CardContent}>
          <Stack alignItems='center' sx={{ height: '100%' }}>
            <Avatar src={s.linkToProfilePicture ?? undefined} className={styles.avatar}>
              {getInitials(s.preferedDisplayName || s.name)}
            </Avatar>

            <Typography className={styles.staffName}>{s.preferedDisplayName || s.name}</Typography>

            {s.speciality ? (
              <Typography className={styles.staffSpecialty}>{s.speciality}</Typography>
            ) : (
              <Box className={styles.specialtySpacer} />
            )}

            {/* ⬇ pushes availability + button to bottom */}
            <Box className={styles.cardSpacer} />

            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              className={`${styles.availabilityRow} ${isAvailable ? styles.available : styles.unavailable}`}
            >
              {isAvailable ? <HeavyCheckIcon /> : <CircleCrossIcon />}
              <Typography variant='body2'>{isAvailable ? t('available') : t('unavailable')}</Typography>
            </Stack>

            <Button
              variant='contained'
              disabled={!isAvailable}
              fullWidth
              className={styles.checkInButton}
              onClick={() => navigate(`/${s.roomName}`)}
            >
              {t('checkIn')}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <Box className={styles.container}>
        {/* Client Recognition */}
        <Box className={styles.clientHeader}>
          <Box className={styles.clientHeaderText}>
            <Typography variant='h1'>{t('via3Home.departmentTitle')}</Typography>
            <Typography className={styles.clientSubTitle}>{t('via3Home.courtServiceUnit')}</Typography>
            <Typography className={styles.clientSubTitle2}>{t('via3Home.afterHoursIntake')}</Typography>
          </Box>
        </Box>

        <Box className={styles.sectionHeader}>
          <Box className={styles.sectionDivider} />
          <Typography sx={{ fontSize: '1.8rem', fontWeight: 700 }}>{t('via3Home.availableStaff')}</Typography>
          <Box className={styles.sectionDivider} />
        </Box>

        <Box className={styles.cardGrid}>{availableStaff.map(renderCard)}</Box>

        <Box className={styles.sectionHeader}>
          <Box className={styles.sectionDivider} />
          <Typography sx={{ fontSize: '1.8rem', fontWeight: 700 }}>{t('via3Home.unavailableStaff')}</Typography>
          <Box className={styles.sectionDivider} />
        </Box>

        <Box className={styles.cardGrid}>{unavailableStaff.map(renderCard)}</Box>
      </Box>
    </>
  );
}
